/**
 * What a classic crosstab's conversion could not bring, stored on the AI crosstab it became.
 * M4S-5777, X144, decision K5. Spec: docs/pdlc/crosstabs-classic-conversion.md §6.
 *
 * `ai_layout.state.conversion` is record state, not a setting: written by the import
 * (`applyLegacyMigration`), carried by every later save unchanged, and shown on reopen so the
 * person who opens the crosstab next can see what stayed in the classic crosstab. It is parsed on
 * the write as on the read, because a POST body is as untrusted as the column.
 *
 * Pure: no React, no fetch.
 */

/** Whether a left-out item can change a number on the table, or only how it reads. */
export type NotBroughtImpact = 'numbers' | 'display';

/** One thing the conversion left in the classic crosstab, in words a researcher can act on. */
export interface NotBroughtItem {
    /** A stable slug for the kind of item (`missing-question`, `filter-tabs`, …). */
    kind: string;
    impact: NotBroughtImpact;
    /** The sentence the dialog, the toast and the reopen notice show. */
    text: string;
    /** The survey code it concerns, when it concerns one. */
    qid?: string;
}

/**
 * M4S-5777 X119: what a report was brought from. Absent on every record written before: a classic
 * conversion. `import-report.ts` is the one home of the words each reads as.
 */
export type ImportSource = 'classic' | 'workbook' | 'sotg' | 'banner-plan' | 'copy';

const IMPORT_SOURCES: readonly ImportSource[] = [
    'classic',
    'workbook',
    'sotg',
    'banner-plan',
    'copy',
];

/**
 * `ai_layout.state.conversion`: what a crosstab could not bring from what it was built from. Since
 * M4S-5777 X119 it is the one "left behind" report of every import (`import-report.ts`).
 */
export interface ConversionRecord {
    /** The source's key (a classic crosstab, a copied crosstab) or file name. It is never written. */
    fromLegacyKey: string;
    /** The classic crosstab's name as it was listed. */
    fromName: string;
    /** When the import ran, ISO 8601. */
    convertedAt: string;
    /** Number-changing items first (`sortNotBrought`). */
    notBrought: NotBroughtItem[];
    /** What it was brought from. Absent: a classic conversion. */
    source?: ImportSource;
}

const TEXT_MAX = 500;
const ITEMS_MAX = 500;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

function text(raw: unknown, max = TEXT_MAX): string {
    return typeof raw === 'string' ? raw.trim().slice(0, max) : '';
}

/** Number-changing items first, each group in the order it was found. */
export function sortNotBrought(items: readonly NotBroughtItem[]): NotBroughtItem[] {
    return [
        ...items.filter((i) => i.impact === 'numbers'),
        ...items.filter((i) => i.impact !== 'numbers'),
    ];
}

function parseItem(raw: unknown): NotBroughtItem | null {
    if (!isPlainObject(raw)) return null;
    const kind = text(raw.kind, 64);
    const sentence = text(raw.text);
    if (!kind || !sentence) return null;
    const qid = text(raw.qid, 128);
    return {
        kind,
        impact: raw.impact === 'numbers' ? 'numbers' : 'display',
        text: sentence,
        ...(qid ? { qid } : {}),
    };
}

/** Untrusted JSON to a well-formed record, or null. Never throws. */
export function parseConversion(raw: unknown): ConversionRecord | null {
    if (!isPlainObject(raw)) return null;
    const fromLegacyKey = text(raw.fromLegacyKey, 128);
    if (!fromLegacyKey) return null;
    const items = Array.isArray(raw.notBrought) ? raw.notBrought : [];
    const notBrought: NotBroughtItem[] = [];
    for (const entry of items) {
        if (notBrought.length >= ITEMS_MAX) break;
        const parsed = parseItem(entry);
        if (parsed) notBrought.push(parsed);
    }
    const source = IMPORT_SOURCES.find((s) => s === raw.source && s !== 'classic');
    return {
        fromLegacyKey,
        fromName: text(raw.fromName),
        convertedAt: text(raw.convertedAt, 64),
        notBrought: sortNotBrought(notBrought),
        ...(source ? { source } : {}),
    };
}
